import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LabelList,
} from "recharts";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ZoomOutMapRoundedIcon from "@mui/icons-material/ZoomOutMapRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import config from "./chatAssistantConfig";
import { downloadCsv, downloadNodeAsJpeg, exportBaseName } from "./downloadUtils";
import ShareResultDialog from "./ShareResultDialog";

/**
 * In-chat pie / line / bar chart for one assistant answer.
 *
 * The server sends `chart` = { type, xKey, series[], stacked } alongside the
 * rows, having already checked those keys against the real result columns, so
 * this component can trust them and concentrate on drawing.
 *
 * Three things the user can do here:
 *   - click the chart to open it large (the 400 px chat panel is for recognition,
 *     the dialog is for reading),
 *   - download the picture on its own as a JPEG,
 *   - download the picture *and* the numbers behind it (JPEG + CSV).
 *
 * COLOUR
 * ----------------------------------------------------------------------------
 * Two palettes, both drawn from the Material hues the dashboards already use
 * (DemandDashboard.js, DashboardCharts.js) so a MatchIQ chart looks like it
 * belongs to this application. Both were checked with the data-visualisation
 * validator for lightness band, chroma floor, colour-vision-deficiency
 * separation and contrast against a white surface:
 *
 *   SERIES_COLORS   - up to four series on a bar/line chart. Validated on
 *                     adjacent pairs, which is what matters when series sit
 *                     side by side and carry a legend.
 *   CATEGORY_COLORS - pie slices, where every slice touches every other, so
 *                     this set is validated on *all* pairs. Only five hues
 *                     survive that bar, which is why a pie keeps the top five
 *                     categories and groups the rest as "Other".
 *
 * Hues are assigned in fixed order and never cycled - the sixth slice is
 * "Other" in neutral grey, not a sixth hue.
 */

/** Adjacent-pair validated. Leads with the app's blue. */
const SERIES_COLORS = ["#1E88E5", "#EF6C00", "#8E24AA", "#43A047"];

/** All-pairs validated - safe for a pie, where no two slices are "non-adjacent". */
const CATEGORY_COLORS = ["#1E88E5", "#EF6C00", "#43A047", "#8E24AA", "#C2185B"];

/** Neutral for the grouped remainder. Deliberately recessive, never a series. */
const OTHER_COLOR = "#9E9E9E";

const MAX_PIE_SLICES = CATEGORY_COLORS.length;

const GRID_COLOR = "#E6EBF1";
const AXIS_TEXT = "#6B7A8D";

/** 12,345 / 1.2M - keeps a 400 px axis legible. */
function compactNumber(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return value;
  const abs = Math.abs(value);
  if (abs >= 1e6) return `${(value / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
  if (abs >= 1e4) return `${(value / 1e3).toFixed(0)}k`;
  return value.toLocaleString("en-US");
}

function fullNumber(value) {
  return typeof value === "number" ? value.toLocaleString("en-US") : value;
}

/** Shortens a long category so the axis does not turn into a wall of text. */
function shortLabel(value, max) {
  const s = value === null || value === undefined || value === "" ? "—" : String(value);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Matches the app's tooltip styling rather than Recharts' default box. */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <Paper
      elevation={3}
      sx={{ px: 1, py: 0.6, border: "1px solid #e3eaf2", maxWidth: 260 }}
    >
      <Typography
        sx={{ fontSize: "0.68rem", fontWeight: 700, color: "text.primary", mb: 0.25 }}
      >
        {label === undefined || label === "" ? payload[0].name : String(label)}
      </Typography>
      {payload.map((entry) => (
        <Box
          key={entry.dataKey || entry.name}
          sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "2px",
              bgcolor: entry.color || entry.payload?.fill,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
            {entry.name}:{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
              {fullNumber(entry.value)}
            </Box>
            {entry.payload && entry.payload.__sharePct !== undefined
              ? ` (${entry.payload.__sharePct}%)`
              : ""}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

export default function ChatChart({
  title,
  chart,
  columns,
  rows,
  allRows,
  rowCount,
  truncated,
  hitCeiling,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  // The JPEG is always rasterised from a dedicated off-screen copy rather than
  // from whichever card is on screen. That keeps the exported picture free of
  // the toolbar icons, and - more importantly - gives it full category labels
  // even when the user exports from the cramped 400 px panel, where those
  // labels are deliberately suppressed.
  const exportRef = useRef(null);

  const { type, xKey, series, stacked } = chart || {};
  const multiSeries = (series || []).length > 1;

  const exportRows = useMemo(
    () => (allRows && allRows.length ? allRows : rows),
    [allRows, rows]
  );

  /**
   * Pie data: numbers must be non-negative for an angle to mean anything, and
   * only the top five categories get a hue - the remainder is summed into
   * "Other" so the palette is never cycled. The table and the CSV still carry
   * every row, which is what the caption points at.
   */
  const pieData = useMemo(() => {
    if (type !== "pie" || !series || !series.length) return [];
    const key = series[0];

    const cleaned = rows
      .map((r) => ({
        name: r[xKey] === null || r[xKey] === undefined || r[xKey] === "" ? "—" : String(r[xKey]),
        value: typeof r[key] === "number" ? r[key] : Number(r[key]),
      }))
      .filter((d) => Number.isFinite(d.value) && d.value > 0)
      .sort((a, b) => b.value - a.value);

    const head = cleaned.slice(0, MAX_PIE_SLICES);
    const tail = cleaned.slice(MAX_PIE_SLICES);
    const data =
      tail.length > 1
        ? [
            ...head,
            {
              name: `Other (${tail.length})`,
              value: tail.reduce((sum, d) => sum + d.value, 0),
              isOther: true,
            },
          ]
        : cleaned;

    const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
    return data.map((d) => ({ ...d, __sharePct: ((d.value / total) * 100).toFixed(1) }));
  }, [type, rows, xKey, series]);

  const groupedIntoOther =
    type === "pie" && pieData.some((d) => d.isOther);

  const handleDownload = async (withData) => {
    setMenuAnchor(null);
    try {
      await downloadNodeAsJpeg(exportRef.current, title);
      if (withData) downloadCsv(columns, exportRows, title);
      setToast({
        severity: "success",
        text: withData
          ? "Chart saved as JPEG, data saved as CSV."
          : "Chart saved as JPEG.",
      });
    } catch (err) {
      setToast({
        severity: "error",
        text: "I could not save the chart image. Please try again.",
      });
    }
  };

  if (!chart || !type || !series || !series.length || !rows || !rows.length) {
    return null;
  }

  const crowdedAxis = type !== "pie" && rows.length > 12;

  /** The chart itself. `large` drives label density, not a different chart. */
  const renderChart = (large) => {
    const axisFont = large ? 12 : 10;
    const xMax = large ? 18 : 10;
    // Too many categories to label at 400 px - the tooltip and the expanded
    // view carry the names instead of a row of overlapping stubs.
    const hideXLabels = crowdedAxis && !large;

    if (type === "pie") {
      return (
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={large ? "38%" : "34%"}
            outerRadius={large ? "72%" : "76%"}
            paddingAngle={1.5}
            stroke="#ffffff"
            strokeWidth={2}
            isAnimationActive={false}
            // Direct share labels are the secondary encoding that keeps the
            // slices distinguishable without relying on colour alone. Drawn as
            // explicit <text> so the label wears neutral ink - Recharts would
            // otherwise paint it in the slice's own colour, which reads poorly
            // for the lighter hues and in an exported JPEG.
            label={(d) => (
              <text
                x={d.x}
                y={d.y}
                fill={AXIS_TEXT}
                textAnchor={d.textAnchor}
                dominantBaseline="central"
                fontSize={axisFont}
              >
                {large
                  ? `${shortLabel(d.name, 18)} ${(d.percent * 100).toFixed(1)}%`
                  : `${(d.percent * 100).toFixed(0)}%`}
              </text>
            )}
            labelLine={large ? { stroke: "#C3D4EA" } : false}
          >
            {pieData.map((entry, i) => (
              <Cell
                key={entry.name}
                fill={entry.isOther ? OTHER_COLOR : CATEGORY_COLORS[i % CATEGORY_COLORS.length]}
              />
            ))}
          </Pie>
          <RechartsTooltip content={<ChartTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={large ? 36 : 28}
            iconType="circle"
            iconSize={8}
            formatter={(value) => (
              <span style={{ fontSize: axisFont, color: AXIS_TEXT }}>
                {shortLabel(value, large ? 28 : 16)}
              </span>
            )}
          />
        </PieChart>
      );
    }

    if (type === "line") {
      return (
        <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={GRID_COLOR} vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: axisFont, fill: AXIS_TEXT }}
            tickFormatter={(v) => shortLabel(v, xMax)}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
            interval="preserveStartEnd"
            minTickGap={large ? 12 : 28}
          />
          <YAxis
            tick={{ fontSize: axisFont, fill: AXIS_TEXT }}
            tickFormatter={compactNumber}
            tickLine={false}
            axisLine={false}
            width={large ? 56 : 40}
          />
          <RechartsTooltip content={<ChartTooltip />} />
          {multiSeries && (
            <Legend
              verticalAlign="top"
              height={24}
              iconType="plainline"
              iconSize={12}
              formatter={(value) => (
                <span style={{ fontSize: axisFont, color: AXIS_TEXT }}>{value}</span>
              )}
            />
          )}
          {series.map((s, i) => (
            <Line
              key={s}
              type="monotone"
              dataKey={s}
              stroke={SERIES_COLORS[i]}
              strokeWidth={2}
              dot={{ r: large ? 4 : 0, strokeWidth: 0, fill: SERIES_COLORS[i] }}
              activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      );
    }

    // Bar
    return (
      <BarChart
        data={rows}
        margin={{ top: large ? 18 : 8, right: 12, bottom: 4, left: 0 }}
        barCategoryGap="22%"
      >
        <CartesianGrid stroke={GRID_COLOR} vertical={false} />
        <XAxis
          dataKey={xKey}
          tick={hideXLabels ? false : { fontSize: axisFont, fill: AXIS_TEXT }}
          tickFormatter={(v) => shortLabel(v, xMax)}
          tickLine={false}
          axisLine={{ stroke: GRID_COLOR }}
          interval={0}
          angle={rows.length > (large ? 10 : 5) ? -35 : 0}
          textAnchor={rows.length > (large ? 10 : 5) ? "end" : "middle"}
          height={
            hideXLabels ? 8 : rows.length > (large ? 10 : 5) ? (large ? 70 : 52) : 24
          }
        />
        <YAxis
          tick={{ fontSize: axisFont, fill: AXIS_TEXT }}
          tickFormatter={compactNumber}
          tickLine={false}
          axisLine={false}
          width={large ? 56 : 40}
        />
        <RechartsTooltip
          content={<ChartTooltip />}
          cursor={{ fill: "rgba(30,136,229,0.06)" }}
        />
        {multiSeries && (
          <Legend
            verticalAlign="top"
            height={24}
            iconType="square"
            iconSize={9}
            formatter={(value) => (
              <span style={{ fontSize: axisFont, color: AXIS_TEXT }}>{value}</span>
            )}
          />
        )}
        {series.map((s, i) => (
          <Bar
            key={s}
            dataKey={s}
            stackId={stacked ? "stack" : undefined}
            fill={SERIES_COLORS[i]}
            // Rounded data-end on the top bar only, so a stack still reads as
            // one column rather than a pile of pills.
            radius={stacked && i < series.length - 1 ? 0 : [4, 4, 0, 0]}
            stroke="#ffffff"
            strokeWidth={stacked ? 2 : 0}
            isAnimationActive={false}
            maxBarSize={large ? 64 : 38}
          >
            {/* One value per bar is readable on a single-series chart with room;
                on anything busier the tooltip carries the numbers. */}
            {large && !multiSeries && rows.length <= 12 && (
              <LabelList
                dataKey={s}
                position="top"
                formatter={compactNumber}
                style={{ fontSize: 11, fill: AXIS_TEXT }}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    );
  };

  const downloadMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={Boolean(menuAnchor)}
      onClose={() => setMenuAnchor(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <MenuItem onClick={() => handleDownload(false)} dense>
        <ListItemIcon sx={{ minWidth: "28px !important" }}>
          <ImageOutlinedIcon sx={{ fontSize: "1.05rem" }} />
        </ListItemIcon>
        <ListItemText
          primary="Graph only"
          secondary="JPEG image"
          primaryTypographyProps={{ fontSize: "0.8rem" }}
          secondaryTypographyProps={{ fontSize: "0.68rem" }}
        />
      </MenuItem>
      <MenuItem onClick={() => handleDownload(true)} dense>
        <ListItemIcon sx={{ minWidth: "28px !important" }}>
          <TableChartOutlinedIcon sx={{ fontSize: "1.05rem" }} />
        </ListItemIcon>
        <ListItemText
          primary="Graph with data"
          secondary={`JPEG + CSV (${exportRows.length.toLocaleString("en-US")} rows)`}
          primaryTypographyProps={{ fontSize: "0.8rem" }}
          secondaryTypographyProps={{ fontSize: "0.68rem" }}
        />
      </MenuItem>
    </Menu>
  );

  /** Header shown above both the inline chart and the expanded one. */
  const header = (large) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: large ? "0.9rem" : "0.72rem",
            color: config.COLORS.primary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={title}
        >
          {title}
        </Typography>
        <Chip
          size="small"
          label={`${rowCount.toLocaleString("en-US")} ${rowCount === 1 ? "category" : "categories"}`}
          sx={{ height: 18, fontSize: "0.65rem", flexShrink: 0 }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {!large && <Tooltip title="Email this graph"><IconButton size="small" onClick={(event) => { event.stopPropagation(); setShareOpen(true); }} aria-label="Email this graph" sx={{ p: .3, color: config.COLORS.primary }}><MailOutlineRoundedIcon sx={{ fontSize: "1rem" }} /></IconButton></Tooltip>}
        {!large && (
          <Tooltip title="Expand chart">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(true);
              }}
              aria-label="Expand chart"
              sx={{ p: 0.3, color: config.COLORS.primary }}
            >
              <ZoomOutMapRoundedIcon sx={{ fontSize: "0.95rem" }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Download chart">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            aria-label="Download chart"
            sx={{ p: 0.3, color: config.COLORS.primary }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
        {large && (
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={() => setExpanded(false)}
              aria-label="Close expanded chart"
              sx={{ p: 0.3 }}
            >
              <CloseRoundedIcon sx={{ fontSize: "1.05rem" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const caption = (
    <>
      {crowdedAxis && !expanded && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.4, color: "text.secondary", fontSize: "0.65rem" }}
        >
          {rowCount.toLocaleString("en-US")} categories — click the chart to expand it
          and read the labels.
        </Typography>
      )}
      {groupedIntoOther && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.4, color: "text.secondary", fontSize: "0.65rem" }}
        >
          The smallest categories are grouped as “Other”. Download the data for the
          full breakdown.
        </Typography>
      )}
      {truncated && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.4, color: "text.secondary", fontSize: "0.65rem" }}
        >
          Charted the first {rows.length.toLocaleString("en-US")} of{" "}
          {rowCount.toLocaleString("en-US")} categories. Download the data for the
          full set.
        </Typography>
      )}
      {hitCeiling && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.4, color: "warning.main", fontSize: "0.65rem" }}
        >
          This result reached the maximum row limit, so it may be incomplete.
        </Typography>
      )}
    </>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Paper
        elevation={0}
        onClick={() => setExpanded(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded(true);
          }
        }}
        aria-label={`${title}. Open the chart in a larger view.`}
        sx={{
          p: 0.75,
          bgcolor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          cursor: "zoom-in",
          "&:hover": { borderColor: `${config.COLORS.primary}66` },
        }}
      >
        {header(false)}
        <Box sx={{ width: "100%", height: config.CHART_HEIGHT }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderChart(false)}
          </ResponsiveContainer>
        </Box>
      </Paper>

      {caption}

      {/* -------------------------------------------------- expanded view */}
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        maxWidth="lg"
        fullWidth
        aria-label="Expanded chart"
      >
        <DialogContent sx={{ p: 2 }}>
          <Paper elevation={0} sx={{ p: 1, bgcolor: "#fff" }}>
            {header(true)}
            <Box sx={{ width: "100%", height: config.CHART_EXPANDED_HEIGHT }}>
              <ResponsiveContainer width="100%" height="100%">
                {renderChart(true)}
              </ResponsiveContainer>
            </Box>
          </Paper>
          {caption}
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------- off-screen export copy
          Off-canvas rather than display:none, because a hidden element has no
          size and Recharts' ResponsiveContainer would render an empty chart.

          The offset lives on the WRAPPER, never on the captured node: the
          rasteriser copies the captured element's own styles into the image it
          builds, so a `left:-20000px` on that element would push the content
          outside the picture and produce a blank JPEG. */}
      <Box
        aria-hidden="true"
        sx={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }}
      >
      <Box
        ref={exportRef}
        sx={{ width: 960, height: 540, bgcolor: "#fff", p: 2 }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.05rem",
            color: config.COLORS.primary,
            mb: 0.75,
          }}
        >
          {title}
          <Box
            component="span"
            sx={{ fontWeight: 400, fontSize: "0.8rem", color: "#6B7A8D", ml: 1 }}
          >
            {rowCount.toLocaleString("en-US")}{" "}
            {rowCount === 1 ? "category" : "categories"}
          </Box>
        </Typography>
        <Box sx={{ width: "100%", height: 470 }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderChart(true)}
          </ResponsiveContainer>
        </Box>
      </Box>
      </Box>

      {downloadMenu}

      <ShareResultDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={title || "MatchIQ chart"}
        resultType="Graph"
        buildAttachment={async () => {
          if (!exportRef.current) throw new Error("The graph is not ready yet");
          const { toJpeg } = await import("html-to-image");
          const dataUrl = await toJpeg(exportRef.current, { quality: .95, backgroundColor: "#ffffff", pixelRatio: 2, cacheBust: true });
          return { filename: `${exportBaseName(title)}.jpeg`, contentType: "image/jpeg", base64: dataUrl.split(",")[1] };
        }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast ? toast.severity : "success"}
          variant="filled"
          sx={{ fontSize: "0.78rem" }}
          onClose={() => setToast(null)}
        >
          {toast ? toast.text : ""}
        </Alert>
      </Snackbar>
    </Box>
  );
}
